import type { KyResponse } from 'ky';
import apiClient from './client';
import {
    ApiErrorBody,
    ApiSuccess,
    AutomationLimit,
    AutomationRule,
    BudgetPermission,
    Candidate,
    ChargeListResponse,
    ChargePolicy,
    ConfirmGiftPaymentRequest,
    ConfirmGiftPaymentResponse,
    CreateChargeRequest,
    CreateChargeResponse,
    CreateRuleRequest,
    ExecutionsResponse,
    GiftCatalogProduct,
    GiftDetail,
    GiftHistoryQuery,
    GiftHistoryResponse,
    GiftSettings,
    GiftSummary,
    HeldItem,
    Paging,
    PointAccount,
    RuleMutationResponse,
    Segment,
    SendGiftRequest,
    SendGiftResponse,
    TopProductsResponse,
    UpdateBudgetPermissionRequest,
    UpdateLimitRequest,
    UpdateRuleRequest,
} from '../types/gifticon';

// 기프티콘 도메인 API — 스펙: docs/Gifticon_Backend_AsBuilt_Guide.md
// 성공 봉투는 { success: true, data } (타 도메인의 { status, data }와 다름) — 언랩은 동일하게 response.data.data
// 공용 apiClient(ky, src/api/client.ts)를 사용 — 센터 열람 모드 쓰기 차단·401 토큰 갱신 인터셉터를 탄다.

const BASE = () => 'v1/gifticon';

const headers = (accessToken: string) => ({ authorization: accessToken });

const buildUrl = (base: string, params: Record<string, string | number | boolean | undefined>) => {
    const qs = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
        .join('&');
    return qs ? `${base}?${qs}` : base;
};

const unwrap = async <T>(response: KyResponse): Promise<T> => {
    const body = await response.json<ApiSuccess<T>>();
    return body.data;
};

/** ky 에러(src/api/client.ts afterResponse)에서 기프티콘 에러 본문({ code, message, errorCode?, details? }) 추출. 아니면 null */
export const parseGiftError = (error: unknown): ApiErrorBody | null => {
    const body = (error as { body?: unknown } | null | undefined)?.body;
    if (body && typeof body === 'object' && 'message' in (body as Record<string, unknown>)) {
        return body as ApiErrorBody;
    }
    return null;
};

// ─────────────── §2 포인트 · 충전 ───────────────

export const getGiftPointAccountAPI = async (accessToken: string): Promise<PointAccount> => {
    const response = await apiClient.get(`${BASE()}/point-account`, { headers: headers(accessToken) });
    return unwrap<PointAccount>(response);
};

export const getGiftChargePolicyAPI = async (accessToken: string): Promise<ChargePolicy> => {
    const response = await apiClient.get(`${BASE()}/charge-policy`, { headers: headers(accessToken) });
    return unwrap<ChargePolicy>(response);
};

export const createGiftChargeAPI = async (
    accessToken: string,
    request: CreateChargeRequest,
): Promise<CreateChargeResponse> => {
    const response = await apiClient.post(`${BASE()}/charges`, { headers: headers(accessToken), json: request });
    return unwrap<CreateChargeResponse>(response);
};

export const getGiftChargesAPI = async (accessToken: string, paging: Paging = {}): Promise<ChargeListResponse> => {
    const response = await apiClient.get(buildUrl(`${BASE()}/charges`, { ...paging }), {
        headers: headers(accessToken),
    });
    return unwrap<ChargeListResponse>(response);
};

// ─────────────── §3 세그먼트 · 설정 ───────────────

export const getGiftSegmentsAPI = async (accessToken: string): Promise<Segment[]> => {
    const response = await apiClient.get(`${BASE()}/segments`, { headers: headers(accessToken) });
    return (await unwrap<Segment[]>(response)) ?? [];
};

export const getGiftCandidatesAPI = async (accessToken: string, segments: string): Promise<Candidate[]> => {
    const response = await apiClient.get(buildUrl(`${BASE()}/candidates`, { segments }), {
        headers: headers(accessToken),
    });
    return (await unwrap<Candidate[]>(response)) ?? [];
};

export const setSegmentRecommendedProductAPI = async (
    accessToken: string,
    segmentKey: string,
    productId: string,
): Promise<void> => {
    await apiClient.put(`${BASE()}/segments/${segmentKey}/recommended-product`, {
        headers: headers(accessToken),
        json: { productId },
    });
};

export const getGiftSettingsAPI = async (accessToken: string): Promise<GiftSettings> => {
    const response = await apiClient.get(`${BASE()}/settings`, { headers: headers(accessToken) });
    return unwrap<GiftSettings>(response);
};

export const updateGiftSettingsAPI = async (accessToken: string, settings: GiftSettings): Promise<GiftSettings> => {
    const response = await apiClient.put(`${BASE()}/settings`, { headers: headers(accessToken), json: settings });
    return unwrap<GiftSettings>(response);
};

// ─────────────── §4 상품 ───────────────

export const getGiftProductsAPI = async (accessToken: string): Promise<GiftCatalogProduct[]> => {
    const response = await apiClient.get(`${BASE()}/products`, { headers: headers(accessToken) });
    return (await unwrap<GiftCatalogProduct[]>(response)) ?? [];
};

// ─────────────── §5 발송 ───────────────

// 발송·결제확정은 다우 발급이 동기로 포함될 수 있어 ky 기본 10초보다 길게 잡는다
// (대량 건은 서버가 백그라운드로 전환해 즉시 응답하지만, 경계 인원의 동기 처리를 방어)
const SEND_TIMEOUT_MS = 5 * 60_000;

/**
 * 서버가 `expectedUnitPrice` 를 **아직 모르는가**.
 *
 * validate.js 는 allowUnknown 없이 `Joi.object().keys()` 를 쓰므로, 이 필드를 모르는 서버는
 * `"expectedUnitPrice" is not allowed` 로 400 을 낸다(실측 확인). 이건 발송 실패가 아니라
 * **요청이 서버에 닿기도 전에 거절된 것**이다 — 배치도, 차감도, 발급도 일어나지 않았다.
 */
const isExpectedPriceUnsupported = (error: unknown): boolean => {
    const body = (error as { body?: { code?: number; message?: string } } | null | undefined)?.body;
    if (!body || body.code !== 400) return false;
    return typeof body.message === 'string' && body.message.includes('expectedUnitPrice');
};

export const sendGiftAPI = async (accessToken: string, request: SendGiftRequest): Promise<SendGiftResponse> => {
    // idempotencyKey 는 헤더 전용 — 바디에 넣으면 백엔드 Joi(allowUnknown 없음)가 400 을 낸다
    const { idempotencyKey, ...body } = request;
    const post = (json: Record<string, unknown>) =>
        apiClient.post(`${BASE()}/gifts`, {
            headers: {
                ...headers(accessToken),
                ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
            },
            json,
            timeout: SEND_TIMEOUT_MS,
        });

    try {
        return unwrap<SendGiftResponse>(await post(body));
    } catch (error) {
        /*
         * 가격 검증을 모르는 서버로 나간 경우에만, **그 필드만 빼고** 한 번 더 보낸다.
         *
         * 자동 재전송을 금지하는 규칙은 "이미 서버에서 무언가 일어났을 수 있는 요청"에 대한 것이다.
         * 이 거절은 Joi 단계라 아무 일도 일어나지 않았고, 멱등키도 그대로 쓴다.
         * 이렇게 해야 프론트가 백엔드보다 먼저 나가도 발송이 죽지 않는다.
         */
        if (body.expectedUnitPrice === undefined || !isExpectedPriceUnsupported(error)) throw error;
        const { expectedUnitPrice, ...withoutPrice } = body;
        return unwrap<SendGiftResponse>(await post(withoutPrice));
    }
};

export const confirmGiftBatchPaymentAPI = async (
    accessToken: string,
    batchId: string,
    request: ConfirmGiftPaymentRequest,
): Promise<ConfirmGiftPaymentResponse> => {
    const response = await apiClient.post(`${BASE()}/gifts/${batchId}/payment-confirm`, {
        headers: headers(accessToken),
        json: request,
        timeout: SEND_TIMEOUT_MS,
    });
    return unwrap<ConfirmGiftPaymentResponse>(response);
};

export const cancelGiftBatchAPI = async (accessToken: string, batchId: string): Promise<void> => {
    await apiClient.delete(`${BASE()}/gifts/${batchId}`, { headers: headers(accessToken) });
};

export const resendGiftItemAPI = async (accessToken: string, batchId: string, itemId: string): Promise<void> => {
    await apiClient.post(`${BASE()}/gifts/${batchId}/items/${itemId}/resend`, { headers: headers(accessToken) });
};

export const cancelGiftItemAPI = async (accessToken: string, batchId: string, itemId: string): Promise<void> => {
    await apiClient.post(`${BASE()}/gifts/${batchId}/items/${itemId}/cancel`, { headers: headers(accessToken) });
};

// ─────────────── §6 발송 내역 ───────────────

export const getGiftHistoryAPI = async (
    accessToken: string,
    query: GiftHistoryQuery = {},
): Promise<GiftHistoryResponse> => {
    const response = await apiClient.get(buildUrl(`${BASE()}/gifts`, { ...query }), {
        headers: headers(accessToken),
    });
    return unwrap<GiftHistoryResponse>(response);
};

export const getGiftDetailAPI = async (accessToken: string, batchId: string): Promise<GiftDetail> => {
    const response = await apiClient.get(`${BASE()}/gifts/${batchId}`, { headers: headers(accessToken) });
    return unwrap<GiftDetail>(response);
};

/** CSV(UTF-8 BOM) 다운로드 — 봉투 없음, item 단위 상한 5,000행 */
export const exportGiftHistoryAPI = async (accessToken: string, query: GiftHistoryQuery = {}): Promise<Blob> => {
    const response = await apiClient.get(buildUrl(`${BASE()}/gifts/export`, { ...query }), {
        headers: headers(accessToken),
    });
    return response.blob();
};

// ─────────────── §7 자동화 ───────────────

export const getAutomationRulesAPI = async (accessToken: string): Promise<AutomationRule[]> => {
    const response = await apiClient.get(`${BASE()}/automation/rules`, { headers: headers(accessToken) });
    return (await unwrap<AutomationRule[]>(response)) ?? [];
};

export const createAutomationRuleAPI = async (
    accessToken: string,
    request: CreateRuleRequest,
): Promise<RuleMutationResponse> => {
    const response = await apiClient.post(`${BASE()}/automation/rules`, {
        headers: headers(accessToken),
        json: request,
    });
    return unwrap<RuleMutationResponse>(response);
};

export const updateAutomationRuleAPI = async (
    accessToken: string,
    ruleId: string,
    request: UpdateRuleRequest,
): Promise<RuleMutationResponse> => {
    const response = await apiClient.put(`${BASE()}/automation/rules/${ruleId}`, {
        headers: headers(accessToken),
        json: request,
    });
    return unwrap<RuleMutationResponse>(response);
};

export const deleteAutomationRuleAPI = async (accessToken: string, ruleId: string): Promise<void> => {
    await apiClient.delete(`${BASE()}/automation/rules/${ruleId}`, { headers: headers(accessToken) });
};

export const getAutomationExecutionsAPI = async (
    accessToken: string,
    ruleId: string,
    paging: Paging = {},
): Promise<ExecutionsResponse> => {
    const response = await apiClient.get(buildUrl(`${BASE()}/automation/rules/${ruleId}/executions`, { ...paging }), {
        headers: headers(accessToken),
    });
    return unwrap<ExecutionsResponse>(response);
};

export const getAutomationLimitAPI = async (accessToken: string): Promise<AutomationLimit> => {
    const response = await apiClient.get(`${BASE()}/automation/limit`, { headers: headers(accessToken) });
    return unwrap<AutomationLimit>(response);
};

export const updateAutomationLimitAPI = async (
    accessToken: string,
    request: UpdateLimitRequest,
): Promise<AutomationLimit> => {
    const response = await apiClient.put(`${BASE()}/automation/limit`, {
        headers: headers(accessToken),
        json: request,
    });
    return unwrap<AutomationLimit>(response);
};

export const getAutomationHeldAPI = async (accessToken: string): Promise<HeldItem[]> => {
    const response = await apiClient.get(`${BASE()}/automation/held`, { headers: headers(accessToken) });
    return (await unwrap<HeldItem[]>(response)) ?? [];
};

export const resendHeldItemAPI = async (accessToken: string, heldId: string): Promise<SendGiftResponse> => {
    const response = await apiClient.post(`${BASE()}/automation/held/${heldId}/resend`, {
        headers: headers(accessToken),
    });
    return unwrap<SendGiftResponse>(response);
};

export const deleteHeldItemAPI = async (accessToken: string, heldId: string): Promise<void> => {
    await apiClient.delete(`${BASE()}/automation/held/${heldId}`, { headers: headers(accessToken) });
};

// ─────────────── §8 강사 권한 ───────────────

export const getTrainerBudgetPermissionsAPI = async (accessToken: string): Promise<BudgetPermission[]> => {
    const response = await apiClient.get(`${BASE()}/trainers/budget-permissions`, { headers: headers(accessToken) });
    return (await unwrap<BudgetPermission[]>(response)) ?? [];
};

export const updateTrainerBudgetPermissionAPI = async (
    accessToken: string,
    trainerId: string,
    request: UpdateBudgetPermissionRequest,
): Promise<BudgetPermission> => {
    const response = await apiClient.put(`${BASE()}/trainers/${trainerId}/budget-permission`, {
        headers: headers(accessToken),
        json: request,
    });
    return unwrap<BudgetPermission>(response);
};

// ─────────────── §9 요약 · 통계 ───────────────

export const getGiftSummaryAPI = async (accessToken: string): Promise<GiftSummary> => {
    const response = await apiClient.get(`${BASE()}/summary`, { headers: headers(accessToken) });
    return unwrap<GiftSummary>(response);
};

export const getGiftTopProductsAPI = async (accessToken: string): Promise<TopProductsResponse> => {
    const response = await apiClient.get(`${BASE()}/stats/top-products`, { headers: headers(accessToken) });
    return unwrap<TopProductsResponse>(response);
};
